/* ============================================================
   Route Profitability Estimator

   The question this answers is not "is the route profitable?" but
   "at what point does it stop being?" — break-even volume, break-even
   rate, and what recovering the empty leg is actually worth.

   Empty running is modelled explicitly because it is usually the single
   largest recoverable margin in road freight and almost never appears
   as a line item in an operator's own costing.
   ============================================================ */

import {
  getAllCorridors,
  getBenchmark,
  getBand,
  compareToBenchmark,
  bandGap
} from '../../data/benchmarks.js';
import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { money, number, percent, signedPercent, parseNumber } from '../../shared/format.js';
import { saveRecord } from '../../shared/records.js';
import { prefillForm, rememberProfile } from '../../shared/profile.js';
import { PrintButton } from '../../components/PrintButton.js';
import { PrefillNotice } from '../../components/PrefillNotice.js';

const plural = (count, unit) => `${unit}${count === 1 ? '' : 's'}`;

const VARIABLE_FIELDS = [
  { id: 'fuel', label: 'Fuel', color: 'var(--ca-chart-8)' },
  { id: 'driver', label: 'Driver & crew', color: 'var(--ca-chart-2)' },
  { id: 'tolls', label: 'Tolls, levies & borders', color: 'var(--ca-chart-1)' },
  { id: 'maintenance', label: 'Maintenance & tyres', color: 'var(--ca-chart-3)' },
  { id: 'port', label: 'Port / ICD handling', color: 'var(--ca-chart-5)' },
  { id: 'other', label: 'Other per-trip', color: 'var(--ca-chart-7)' }
];

export function ProfitabilityPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Route Profitability Estimator</h1>
      <p>
        Margin per trip, break-even volume, and what the empty leg is costing you — against the
        sourced corridor benchmark.
      </p>
    </header>

    <div id="rp-form">
      <section class="section">
        <h2 class="section-title">Route</h2>
        <div class="card">
          <label class="field">
            <span class="field-label">Corridor</span>
            <select id="rp-corridor" class="input-field">
              <option value="">Select a corridor…</option>
            </select>
          </label>
          <div class="grid-2">
            <label class="field">
              <span class="field-label">Distance, one way (km)</span>
              <input id="rp-distance" class="input-field num" type="number" inputmode="numeric"
                     min="1" step="1" placeholder="485" />
            </label>
            <label class="field">
              <span class="field-label">Payload (tonnes)</span>
              <input id="rp-payload" class="input-field num" type="number" inputmode="decimal"
                     min="0.1" step="0.1" placeholder="28" />
            </label>
          </div>
          <label class="field" style="margin-bottom:0;">
            <span class="field-label">Empty running on the return leg (%)</span>
            <input id="rp-empty" class="input-field num" type="number" inputmode="numeric"
                   min="0" max="100" step="5" value="100" />
            <span class="field-hint">
              100% means every return trip runs empty. The estimator prices the recovery.
            </span>
          </label>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Revenue</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Revenue per outbound trip (USD)</span>
              <input id="rp-revenue" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1" placeholder="1400" />
            </label>
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Backhaul revenue, when loaded (USD)</span>
              <input id="rp-backhaul-rev" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1" placeholder="900" />
            </label>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Variable cost, per round trip (USD)</h2>
        <div class="card">
          <div class="grid-2">
            ${VARIABLE_FIELDS.map(
              (f) => html`
                <label class="field">
                  <span class="field-label" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
                    ${f.label}
                  </span>
                  <input id="rp-${f.id}" class="input-field num" type="number"
                         inputmode="decimal" min="0" step="1" placeholder="0" />
                </label>
              `
            )}
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Volume &amp; fixed cost</h2>
        <div class="card">
          <div class="grid-2">
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Round trips per month</span>
              <input id="rp-trips" class="input-field num" type="number" inputmode="numeric"
                     min="1" step="1" placeholder="6" />
            </label>
            <label class="field" style="margin-bottom:0;">
              <span class="field-label">Fixed cost per month (USD)</span>
              <input id="rp-fixed" class="input-field num" type="number" inputmode="decimal"
                     min="0" step="1" placeholder="2400" />
              <span class="field-hint">Financing, insurance, licences, overhead attributable to this vehicle.</span>
            </label>
          </div>
        </div>
      </section>

      <div id="rp-error"></div>

      <button class="btn btn-primary btn-block" id="rp-calculate" type="button"
              style="padding: var(--ca-space-4);">
        Estimate profitability
      </button>
    </div>

    <div id="profitability-results" hidden></div>
  `;

  const corridorSelect = page.querySelector('#rp-corridor');
  for (const corridor of getAllCorridors()) {
    const option = document.createElement('option');
    option.value = corridor.key;
    option.textContent = `${corridor.name} · ${corridor.distance} km`;
    corridorSelect.appendChild(option);
  }
  corridorSelect.addEventListener('change', () => {
    const bench = getBenchmark(corridorSelect.value);
    if (bench) page.querySelector('#rp-distance').value = String(bench.distance);
  });

  const applied = prefillForm(page, {
    corridor: 'rp-corridor',
    distance: 'rp-distance',
    payload: 'rp-payload',
    tripsPerMonth: 'rp-trips'
  });
  if (applied.length > 0) {
    const notice = PrefillNotice(applied, page);
    if (notice) page.querySelector('#rp-form').prepend(notice);
  }

  page.querySelector('#rp-calculate').addEventListener('click', () => calculate(page));

  return page;
}

function calculate(page) {
  const val = (id) => parseNumber(page.querySelector(`#${id}`).value);

  const corridorKey = page.querySelector('#rp-corridor').value;
  const distance = val('rp-distance');
  const payload = val('rp-payload');
  const emptyPct = Math.min(100, Math.max(0, val('rp-empty') ?? 100));
  const revenue = val('rp-revenue');
  const backhaulRevenue = val('rp-backhaul-rev') ?? 0;
  const trips = val('rp-trips');
  const fixedMonthly = val('rp-fixed') ?? 0;

  const variable = {};
  for (const f of VARIABLE_FIELDS) variable[f.id] = val(`rp-${f.id}`) ?? 0;
  const variableTotal = Object.values(variable).reduce((a, b) => a + b, 0);

  const errors = [];
  if (distance === null || distance <= 0) errors.push('Enter a distance greater than zero.');
  if (payload === null || payload <= 0) errors.push('Enter a payload greater than zero.');
  if (revenue === null || revenue <= 0) errors.push('Enter the outbound revenue per trip.');
  if (trips === null || trips <= 0) errors.push('Enter at least one round trip per month.');
  if (variableTotal === 0) errors.push('Enter at least one variable cost component.');

  const errorSlot = page.querySelector('#rp-error');
  if (errors.length > 0) {
    errorSlot.innerHTML = html`
      <div class="notice notice-danger" style="margin-bottom: var(--ca-space-4);">
        <span style="flex-shrink:0;margin-top:2px;">${raw(icon('alert', 15))}</span>
        <div>
          <strong style="font-weight: var(--ca-weight-medium);">Cannot estimate yet</strong>
          <ul style="margin: var(--ca-space-1) 0 0 var(--ca-space-4); font-size: var(--ca-text-xs);">
            ${errors.map((e) => html`<li>${e}</li>`)}
          </ul>
        </div>
      </div>
    `;
    errorSlot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  errorSlot.innerHTML = '';

  const loadedShare = 1 - emptyPct / 100;
  const revenuePerTrip = revenue + backhaulRevenue * loadedShare;
  const contributionPerTrip = revenuePerTrip - variableTotal;

  const monthlyRevenue = revenuePerTrip * trips;
  const monthlyVariable = variableTotal * trips;
  const monthlyProfit = monthlyRevenue - monthlyVariable - fixedMonthly;
  const marginPct = monthlyRevenue > 0 ? (monthlyProfit / monthlyRevenue) * 100 : 0;

  const breakEvenTrips = contributionPerTrip > 0 ? fixedMonthly / contributionPerTrip : null;
  const breakEvenRate = variableTotal + fixedMonthly / trips - backhaulRevenue * loadedShare;

  // Cost per ton-km is charged against the loaded distance only: the
  // empty leg carries no tonnes, so including it would flatter the figure.
  const loadedKm = distance * (1 + loadedShare);
  const tonKm = payload * loadedKm;
  const costPerTonKm = tonKm > 0 ? (variableTotal + fixedMonthly / trips) / tonKm : 0;

  const mode = 'road';
  const comparison = corridorKey ? compareToBenchmark(corridorKey, mode, costPerTonKm) : { available: false, reason: 'No corridor selected.' };
  const band = corridorKey ? getBand(corridorKey, mode) : null;
  const bench = corridorKey ? getBenchmark(corridorKey) : null;

  // What full backhaul recovery would be worth.
  const fullRecoveryProfit =
    (revenue + backhaulRevenue) * trips - monthlyVariable - fixedMonthly;
  const recoveryUpside = fullRecoveryProfit - monthlyProfit;

  renderResults(page, {
    bench,
    band,
    comparison,
    gap: bandGap(costPerTonKm, band, tonKm),
    distance,
    payload,
    emptyPct,
    revenue,
    backhaulRevenue,
    trips,
    fixedMonthly,
    variable,
    variableTotal,
    revenuePerTrip,
    contributionPerTrip,
    monthlyRevenue,
    monthlyProfit,
    marginPct,
    breakEvenTrips,
    breakEvenRate,
    costPerTonKm,
    recoveryUpside
  });

  void saveRecord('profitability', {
    corridor: corridorKey || null,
    monthlyProfit,
    marginPct,
    costPerTonKm
  });

  rememberProfile({
    corridor: corridorKey || null,
    distance,
    payload,
    tripsPerMonth: trips
  });
}

function renderResults(page, r) {
  page.querySelector('#rp-form').hidden = true;

  const results = page.querySelector('#profitability-results');
  results.hidden = false;
  results.className = 'animate-fade-in';
  results.replaceChildren();

  const healthy = r.monthlyProfit > 0;
  const color = healthy ? 'var(--ca-positive)' : 'var(--ca-danger)';

  const head = document.createElement('div');
  head.style.cssText = 'text-align:center; margin-bottom: var(--ca-space-6);';
  head.innerHTML = html`
    <div class="badge" style="background: color-mix(in srgb, ${color} 12%, transparent); color:${color};">
      ${healthy ? 'PROFITABLE' : 'LOSS-MAKING'}
    </div>
    <div class="stat-figure num" style="margin-top: var(--ca-space-3); color:${color};">
      ${money(r.monthlyProfit)}
    </div>
    <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
      per month · <span class="num">${percent(r.marginPct, 1)}</span> net margin on
      <span class="num">${money(r.monthlyRevenue)}</span> revenue
    </div>
  `;
  results.appendChild(head);

  /* ---- Per-trip economics ---- */
  const perTrip = document.createElement('div');
  perTrip.className = 'card';
  perTrip.style.marginBottom = 'var(--ca-space-4)';
  perTrip.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Per round trip</h3>
    <div class="table-wrap">
      <table>
        <tbody>
          <tr><td>Outbound revenue</td><td class="right num">${money(r.revenue)}</td></tr>
          ${r.backhaulRevenue > 0
            ? html`<tr>
                <td>
                  Backhaul revenue
                  <span style="color: var(--ca-text-quaternary); font-size: var(--ca-text-2xs);">
                    (${percent(100 - r.emptyPct)} of returns loaded)
                  </span>
                </td>
                <td class="right num">${money(r.backhaulRevenue * (1 - r.emptyPct / 100))}</td>
              </tr>`
            : ''}
          <tr>
            <td style="font-weight: var(--ca-weight-medium);">Revenue</td>
            <td class="right num" style="font-weight: var(--ca-weight-medium);">${money(r.revenuePerTrip)}</td>
          </tr>
          <tr><td>Variable cost</td><td class="right num">−${money(r.variableTotal)}</td></tr>
          <tr>
            <td style="font-weight: var(--ca-weight-medium);">Contribution</td>
            <td class="right num" style="font-weight: var(--ca-weight-medium); color:${r.contributionPerTrip > 0 ? 'var(--ca-positive)' : 'var(--ca-danger)'};">
              ${money(r.contributionPerTrip)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
  results.appendChild(perTrip);

  /* ---- Break-even ---- */
  const breakEven = document.createElement('div');
  breakEven.className = 'card';
  breakEven.style.marginBottom = 'var(--ca-space-4)';
  breakEven.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Break-even</h3>
    <div class="grid-2">
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">
          ${r.breakEvenTrips === null ? '—' : number(Math.ceil(r.breakEvenTrips))}
        </div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          ${r.breakEvenTrips === null
            ? 'never — contribution is negative'
            : `trips per month to cover fixed cost (you run ${r.trips})`}
        </div>
      </div>
      <div>
        <div class="stat-figure num" style="font-size: var(--ca-text-xl);">${money(r.breakEvenRate)}</div>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          minimum outbound rate at <span class="num">${r.trips}</span> trips
        </div>
      </div>
    </div>
    ${r.breakEvenTrips !== null && r.trips > 0
      ? html`
          <div class="progress-track" style="margin-top: var(--ca-space-4);">
            <div
              class="progress-fill"
              style="width: ${Math.min(100, (Math.min(r.breakEvenTrips, r.trips) / r.trips) * 100)}%;
                     background: ${r.breakEvenTrips <= r.trips ? 'var(--ca-positive)' : 'var(--ca-danger)'};"
            ></div>
          </div>
          <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: var(--ca-space-2);">
            ${r.breakEvenTrips <= r.trips
              ? html`Break-even at <span class="num">${number(Math.ceil(r.breakEvenTrips))}</span>
                  ${plural(Math.ceil(r.breakEvenTrips), 'trip')} — you have
                  <span class="num">${number(r.trips - Math.ceil(r.breakEvenTrips))}</span>
                  ${plural(r.trips - Math.ceil(r.breakEvenTrips), 'trip')} of headroom.`
              : html`You need <span class="num">${number(Math.ceil(r.breakEvenTrips) - r.trips)}</span> more
                  ${plural(Math.ceil(r.breakEvenTrips) - r.trips, 'trip')} a month, or a higher rate,
                  to cover fixed cost.`}
          </p>
        `
      : ''}
  `;
  results.appendChild(breakEven);

  /* ---- Empty running ---- */
  if (r.emptyPct > 0 && r.backhaulRevenue > 0) {
    const backhaul = document.createElement('div');
    backhaul.className = 'card';
    backhaul.style.marginBottom = 'var(--ca-space-4)';
    backhaul.style.borderColor = 'color-mix(in srgb, var(--ca-warning) 30%, var(--ca-border))';
    backhaul.innerHTML = html`
      <div class="row" style="margin-bottom: var(--ca-space-3);">
        <span style="color: var(--ca-warning);">${raw(icon('truck', 18))}</span>
        <h3 style="flex:1; font-size: var(--ca-text-sm);">The empty leg</h3>
      </div>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
        <span class="num">${percent(r.emptyPct)}</span> of your return legs run empty. Loading
        every one of them at your stated backhaul rate would add
        <strong class="num" style="color: var(--ca-positive);">${money(r.recoveryUpside)}</strong>
        a month — a
        <span class="num">${signedPercent(r.monthlyProfit !== 0 ? (r.recoveryUpside / Math.abs(r.monthlyProfit)) * 100 : 0)}</span>
        change against current profit.
      </p>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-quaternary);">
        Full recovery is rarely achievable. Halving empty running is the realistic target, and
        would be worth about ${money(r.recoveryUpside / 2)} a month on these figures.
      </p>
    `;
    results.appendChild(backhaul);
  }

  /* ---- Benchmark ---- */
  const benchCard = document.createElement('div');
  benchCard.className = 'card';
  benchCard.style.marginBottom = 'var(--ca-space-4)';
  benchCard.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">Fully-loaded cost per tonne-km</h3>
    <div class="stat-figure num" style="font-size: var(--ca-text-xl);">$${r.costPerTonKm.toFixed(3)}</div>
    <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: var(--ca-space-2);">
      Variable cost plus the fixed cost attributable to one trip, over
      <span class="num">${number(r.payload, 1)}</span> t across the loaded distance only —
      the empty leg carries no tonnes, so counting it would flatter this figure.
    </p>
    ${r.comparison.available
      ? html`
          <div class="notice notice-info" style="margin-top: var(--ca-space-3);">
            <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
            <div style="font-size: var(--ca-text-xs);">
              Against ${r.bench.name} road: low $${r.band.low.toFixed(3)} · median
              $${r.band.median.toFixed(3)} · high $${r.band.high.toFixed(3)}. You are
              <strong style="color:${r.comparison.tone};">${r.comparison.status}</strong>.
              ${r.gap
                ? html`<br /><br />Reaching the median is worth
                    <strong style="font-weight: var(--ca-weight-medium);">
                      ${money(r.gap.toMedian)}</strong
                    >
                    a trip, ${money(r.gap.toMedian * r.trips)} a month at ${r.trips} trips.
                    The low end of the band is worth ${money(r.gap.toLow)} a trip. Two figures
                    rather than one because the band is a range, not a target: the median is what
                    a competent operator on this lane achieves, the low end is what the best do.`
                : ''}
            </div>
          </div>
        `
      : html`
          <div class="notice notice-info" style="margin-top: var(--ca-space-3);">
            <span style="flex-shrink:0;margin-top:2px;">${raw(icon('info', 15))}</span>
            <div style="font-size: var(--ca-text-xs);">${r.comparison.reason}</div>
          </div>
        `}
  `;
  results.appendChild(benchCard);

  results.appendChild(
    PrintButton({
      targetId: 'profitability-results',
      title: 'Route Profitability Estimate',
      subtitle: `${r.bench ? r.bench.name : 'Custom route'} · ${r.trips} trips/month`
    })
  );

  const again = document.createElement('div');
  again.className = 'no-print';
  again.style.cssText = 'text-align:center; margin-top: var(--ca-space-4);';
  const btn = document.createElement('button');
  btn.className = 'btn btn-ghost';
  btn.type = 'button';
  btn.textContent = 'Change the inputs';
  btn.addEventListener('click', () => {
    results.hidden = true;
    page.querySelector('#rp-form').hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  again.appendChild(btn);
  results.appendChild(again);

  results.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
